import type { LabelColor } from 'src/components/label';

import dayjs from 'dayjs';

// ----------------------------------------------------------------------
// Tipos da NFS-e — porte de src/_legacy/types/nfse.ts.
// A LÓGICA de dados/contratos vem do legado; o visual é refeito em Minimal MUI.
//
// Contrato do backend (apps/backend-portal/src/consulta/*.ts):
//  - GET  /portal-api/nfse                     → { notas: NfseListItem[] }
//  - GET  /portal-api/nfse/:id                 → { nota: NfseDetail }
//  - POST /portal-api/nfse/emitir              → { nfse_id, numero }
//  - POST /portal-api/nfse/:id/cancelar        → { success }
//  - GET  /portal-api/nfse/:id/correcoes       → { correcoes: Correcao[] }
//  - POST /portal-api/nfse/:id/correcoes       → { success, numero_correcao }
//  - GET  /portal-api/nfse/:id/exportar-xml    → XML (blob)
//  - GET  /portal-api/nfse/:id/imprimir        → PDF (blob)
//  - GET  /portal-api/nfse/emitir/atividades   → Atividade[]
//  - GET  /portal-api/nfse/emitir/servicos     → Servico[]
//  - GET  /portal-api/nfse/emitir/tomadores    → Tomador[]
//  - GET  /portal-api/nfse/emitir/buscar-pessoa → Pessoa
// ----------------------------------------------------------------------

export interface Atividade {
  id: number;
  codigo: string;
  descricao: string;
}

export interface Servico {
  id: number;
  codigo: string;
  descricao: string;
  // servicoset.aliquota — usado para auto-preencher a alíquota no Emitir.
  aliquota?: number;
}

export interface Tomador {
  id: number;
  nome: string;
  cpf_cnpj: string;
}

export interface Pessoa {
  id: number;
  nome: string;
  cpf_cnpj: string;
}

/**
 * Corpo enviado para POST /nfse/emitir. Os nomes espelham os parâmetros REAIS
 * de rps.gerar_nfse (grupos tm_/sv_/vl_) que o backend aceita e injeta em
 * gerar_nfse. Identidade do prestador (pr_/tr_cpfcnpj), protocolo e numeração do
 * RPS são resolvidos NO SERVIDOR (whitelist) — o cliente não os envia.
 * Obrigatórios: tm_cpfcnpj, sv_discriminacao, vl_servico.
 */
export interface EmitNfseBody {
  tm_cpfcnpj: string;
  tm_razaosocial?: string;
  // Endereço/contato do tomador — vão na nota (whitelist tm_* de rps.gerar_nfse,
  // ver backend/consulta/emit-mapper.ts). Opcionais no contrato; a UI exige os
  // essenciais para uma NFSA-e avulsa correta.
  tm_cep?: string;
  tm_endereco?: string;
  tm_numero?: string;
  tm_complemento?: string;
  tm_bairro?: string;
  tm_uf?: string;
  tm_telefone?: string;
  tm_email?: string;
  sv_discriminacao: string;
  sv_cnae?: string;
  sv_itemlistaservico?: string;
  vl_servico: number;
  vl_aliquota?: number;
  vl_iss?: number;
}

export interface EmitNfseResponse {
  nfse_id: number;
  numero: string;
}

export interface NfseListItem {
  id: number;
  numero: string | null;
  serie: string;
  data_emissao: string;
  valor: number;
  // Backend retorna a situação como inteiro (nfse.nfeemitidaset.situacao).
  situacao: number;
  tomador: { nome: string; cnpj: string } | null;
}

export interface NfseListResponse {
  notas: NfseListItem[];
}

export interface NfseDetail {
  id: number;
  numero: string | null;
  serie: string;
  data_emissao: string;
  competencia: string | null;
  valor: number;
  valor_servicos: number | null;
  iss_valor: number;
  aliquota: number | null;
  ir_valor: number;
  csll_valor: number;
  pis_valor: number | null;
  cofins_valor: number | null;
  inss_valor: number | null;
  outras_retencoes: number | null;
  desconto_condicionado: number | null;
  desconto_incondicionado: number | null;
  // Inteiro (mesma coluna da lista).
  situacao: number;
  autenticacao: string | null;
  descricao: string | null;
  // Endereço do tomador vem embutido na nota (nfse.nfeemitidaset): campos
  // logradouro/numero/bairro/cep/uf — não há `endereco`/`cidade` prontos.
  tomador: {
    nome: string;
    cnpj: string;
    logradouro: string | null;
    numero: string | null;
    bairro: string | null;
    cep: string | null;
    uf: string | null;
  } | null;
  itens: {
    descricao: string;
    quantidade: number;
    valor_unitario: number;
    valor_total: number;
  }[];
}

export interface NfseDetailResponse {
  nota: NfseDetail;
}

export interface Correcao {
  numero_correcao: number;
  motivo: string;
  enunciado: string | null;
  corrigida: boolean;
  realizada: boolean;
  data_correcao: string;
  dt_emissao: string;
}

export interface CorrecoesResponse {
  correcoes: Correcao[];
}

// ----------------------------------------------------------------------
// Situações — a coluna nfse.nfeemitidaset.situacao é um INTEIRO. Códigos
// confirmados ao vivo (docs/portal-contribuinte-migracao/STATUS.md:63-66):
//  - 7  = cancelamento direto (cancel-routes.ts SITUACAO_CANCELADA)
//  - 3  = cancelada via solicitação formal aceita
//  - 11 = em análise (solicitação formal pendente)
//  - demais = nota ativa/emitida
// Só cor por FUNÇÃO. Não inventamos rótulos que o schema não confirma — por
// isso, como no legado (ConsultarRps situacaoLabel), mostramos o código junto.
// ----------------------------------------------------------------------

// Situações canceláveis pelo fluxo direto — espelha cancel-routes.ts e o legado
// hooks.ts. Só nesses estados o botão "Cancelar NFS-e" habilita.
export const SITUACOES_CANCELAVEIS: readonly number[] = [1, 2, 4, 5, 6, 9, 11];
export const SITUACAO_CANCELADA_DIRETA = 7;
export const SITUACAO_CANCELADA_FORMAL = 3;
export const SITUACAO_EM_ANALISE = 11;

export function isCancelavel(situacao: number): boolean {
  return SITUACOES_CANCELAVEIS.includes(situacao);
}

export function isCancelada(situacao: number): boolean {
  return situacao === SITUACAO_CANCELADA_DIRETA || situacao === SITUACAO_CANCELADA_FORMAL;
}

export function getSituacaoLabel(situacao: number): string {
  if (isCancelada(situacao)) return `Cancelada (${situacao})`;
  if (situacao === SITUACAO_EM_ANALISE) return `Em análise (${situacao})`;
  return `Emitida (${situacao})`;
}

export function getSituacaoColor(situacao: number): LabelColor {
  if (isCancelada(situacao)) return 'error';
  if (situacao === SITUACAO_EM_ANALISE) return 'warning';
  return 'success';
}

// ----------------------------------------------------------------------
// Formatação — datas DD/MM/YYYY, valores em BRL (PT-BR).
// ----------------------------------------------------------------------

export function formatDateBr(value: string | null | undefined): string {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD/MM/YYYY') : '—';
}

export function formatCurrency(valor: number | null | undefined): string {
  return (valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
